import { useState, type ReactElement } from "react";
import { Trash2, TriangleAlert } from "lucide-react";
import {
  CONTAINMENT_ACTION_STATUSES,
  CONTAINMENT_EFFECTIVENESS,
  CONTAINMENT_PRIORITIES,
  MAX_CONTAINMENT_NOTES_PER_ACTION,
  type ContainmentActionStatus,
  type ContainmentEffectiveness,
  type ContainmentPlanAction,
  type ContainmentPriority,
  widgetTargetKey,
} from "@playbookai/widget-schemas";
import { useFieldEditSession } from "../field-edit-session.js";
import { WidgetPersonField } from "../person-field.js";
import type { WidgetChangeOptions } from "../types.js";
import { usePeopleSource } from "../user-directory.js";
import { Button } from "../../atoms/Button/Button.js";
import { Input } from "../../atoms/Input/Input.js";
import { Textarea } from "../../atoms/Textarea/Textarea.js";
import { DatePicker } from "../../molecules/DatePicker/DatePicker.js";
import { EmptyState } from "../../molecules/EmptyState/EmptyState.js";
import { Select } from "../../molecules/Select/Select.js";
import { ActionMenu } from "../../molecules/ActionMenu/ActionMenu.js";

import { effectivenessWarning, noteTime } from "./presentation.js";

/**
 * Detail column of the expanded editor. Every field maps 1:1 to
 * `ContainmentPlanAction`. Owner is the shared person field on the canvas's
 * `PeopleSource` — the name plus an optional member link (assign-person d7) —
 * and stays free text where no source is provided.
 */
export function ActionDetailPanel({
  action,
  readOnly,
  onUpdate,
  onDelete,
}: {
  action: ContainmentPlanAction;
  readOnly?: boolean | undefined;
  /** With `grantViewerIfMissing`, a promise that settles with the save. */
  onUpdate: (
    patch: Partial<ContainmentPlanAction>,
    options?: WidgetChangeOptions,
  ) => void | Promise<void>;
  onDelete: () => void;
}): ReactElement {
  const disabled = readOnly === true;
  const people = usePeopleSource();
  const warning = effectivenessWarning(action);
  /**
   * PB-4923 AC5 — one claim per leaf of THIS action, `eta` included.
   *
   * `eta` is edited through the DS `DatePicker`, which reports its OPEN
   * lifetime rather than DOM focus: its trigger blurs the moment the calendar
   * takes focus, and the outside-press path never blurs it at all, so the
   * claim is held from open to close instead.
   */
  const target = (field: string): string =>
    widgetTargetKey([
      { kind: "item", collection: "actions", itemId: action.id },
      { kind: "prop", name: field },
    ]);
  const actionLease = useFieldEditSession(target("action"));
  const ownerLease = useFieldEditSession(target("owner"));
  const statusLease = useFieldEditSession(target("status"));
  const protectedScopeLease = useFieldEditSession(target("protectedScope"));
  const effectivenessLease = useFieldEditSession(target("effectiveness"));
  const verificationMethodLease = useFieldEditSession(
    target("verificationMethod"),
  );
  const successCriteriaLease = useFieldEditSession(target("successCriteria"));
  const evidenceLease = useFieldEditSession(target("effectivenessEvidence"));
  const etaLease = useFieldEditSession(target("eta"));

  return (
    <section className="flex min-h-0 min-w-0 flex-col rounded-lg border border-pb-border bg-pb-card-elevated/30">
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
        <header className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-pb-text-secondary">
            Selected action
          </h3>
          {!disabled && (
            <ActionMenu
              triggerLabel="Action options"
              triggerSize="sm"
              items={[
                {
                  id: "delete",
                  label: "Delete",
                  hint: `Containment action: ${action.action || "untitled"}`,
                  icon: Trash2,
                  tone: "critical",
                  onSelect: onDelete,
                },
              ]}
            />
          )}
        </header>
        <Input
          label="Containment action"
          value={action.action}
          maxLength={255}
          disabled={disabled}
          {...actionLease}
          onChange={(event) => onUpdate({ action: event.target.value })}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {people ? (
            <WidgetPersonField
              source={people}
              label="Owner"
              allowTyped={false}
              text={action.owner}
              userId={action.ownerUserId}
              readOnly={disabled}
              {...ownerLease}
              onChange={(next, options) =>
                onUpdate(
                  { owner: next.text, ownerUserId: next.userId },
                  options,
                )
              }
            />
          ) : (
            <Input
              label="Owner"
              readOnly
              hint="People search is unavailable here."
              value={action.owner}
              maxLength={255}
              disabled={disabled}
            />
          )}
          <DatePicker
            label="ETA"
            value={action.eta}
            disabled={disabled}
            {...etaLease}
            onChange={(iso) => onUpdate({ eta: iso })}
          />

          <Select
            label="Status"
            value={action.status}
            disabled={disabled}
            {...statusLease}
            options={CONTAINMENT_ACTION_STATUSES.map((value) => ({
              value,
              label: value,
            }))}
            onChange={(value) =>
              onUpdate({ status: value as ContainmentActionStatus })
            }
          />
        </div>

        <Input
          label="Protected scope"
          value={action.protectedScope ?? ""}
          maxLength={500}
          disabled={disabled}
          {...protectedScopeLease}
          onChange={(event) => onUpdate({ protectedScope: event.target.value })}
        />
        {warning ? (
          <p
            role="status"
            className="flex items-start gap-1.5 rounded-md border border-pb-red/30 bg-pb-red/10 px-2.5 py-1.5 text-dense-1 text-pb-red-text"
          >
            <TriangleAlert
              aria-hidden="true"
              className="mt-px h-3.5 w-3.5 shrink-0"
            />
            {action.effectivenessEvidence?.trim() || warning}
          </p>
        ) : null}

        <details className="border-t border-pb-border">
          <summary className="cursor-pointer py-2 text-sm text-pb-text-secondary">
            Effectiveness check · {action.effectiveness}
          </summary>
          <div className="space-y-3 pb-4">
            <Select
              label="Effectiveness"
              value={action.effectiveness}
              disabled={disabled}
              {...effectivenessLease}
              options={CONTAINMENT_EFFECTIVENESS.map((value) => ({
                value,
                label: value,
              }))}
              onChange={(value) =>
                onUpdate({ effectiveness: value as ContainmentEffectiveness })
              }
            />
            <Input
              label="Verification method"
              value={action.verificationMethod ?? ""}
              maxLength={500}
              disabled={disabled}
              {...verificationMethodLease}
              onChange={(event) =>
                onUpdate({ verificationMethod: event.target.value })
              }
            />

            <Textarea
              label="Success criteria"
              value={action.successCriteria ?? ""}
              maxLength={500}
              rows={2}
              disabled={disabled}
              {...successCriteriaLease}
              onChange={(event) =>
                onUpdate({ successCriteria: event.target.value })
              }
            />
            <Textarea
              label="Effectiveness evidence"
              value={action.effectivenessEvidence ?? ""}
              maxLength={500}
              rows={2}
              disabled={disabled}
              {...evidenceLease}
              onChange={(event) =>
                onUpdate({ effectivenessEvidence: event.target.value })
              }
            />
          </div>
        </details>
        <details className="border-t border-pb-border">
          <summary className="cursor-pointer py-2 text-sm text-pb-text-secondary">
            Supporting details
          </summary>
          <div className="space-y-3 pb-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="Priority"
                value={action.priority}
                disabled={disabled}
                options={CONTAINMENT_PRIORITIES.map((value) => ({
                  value,
                  label: value,
                }))}
                onChange={(value) =>
                  onUpdate({ priority: value as ContainmentPriority })
                }
              />
              {/* The schema lets this optional date be unset; the picker's
                  clear control beside the trigger does that. */}
              <DatePicker
                label="Started on"
                value={action.startedOn ?? ""}
                disabled={disabled}
                clearable
                clearLabel="Clear started on"
                onChange={(iso) => onUpdate({ startedOn: iso })}
              />
            </div>
            <Input
              label="Verification cadence"
              value={action.verificationCadence ?? ""}
              maxLength={255}
              disabled={disabled}
              onChange={(event) =>
                onUpdate({ verificationCadence: event.target.value })
              }
            />
            <Textarea
              label="Side-effect safeguard"
              value={action.sideEffectSafeguard ?? ""}
              maxLength={500}
              rows={2}
              disabled={disabled}
              onChange={(event) =>
                onUpdate({ sideEffectSafeguard: event.target.value })
              }
            />
            <Textarea
              label="Escalation trigger"
              value={action.escalationTrigger ?? ""}
              maxLength={500}
              rows={2}
              disabled={disabled}
              onChange={(event) =>
                onUpdate({ escalationTrigger: event.target.value })
              }
            />
          </div>
        </details>
        <details className="border-t border-pb-border">
          <summary className="cursor-pointer py-2 text-sm text-pb-text-secondary">
            Notes ({action.notes.length})
          </summary>
          <div className="pb-4">
            <NotesTab action={action} readOnly={disabled} onUpdate={onUpdate} />
          </div>
        </details>
        <details className="border-t border-pb-border">
          <summary className="cursor-pointer py-2 text-sm text-pb-text-secondary">
            Linked records
          </summary>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Input
              label="Milestone ID"
              value={action.milestoneId ?? ""}
              maxLength={64}
              disabled={disabled}
              onChange={(event) =>
                onUpdate({ milestoneId: event.target.value || undefined })
              }
            />
            <Input
              label="Jira key"
              value={action.jiraKey ?? ""}
              maxLength={255}
              disabled={disabled}
              onChange={(event) =>
                onUpdate({ jiraKey: event.target.value || undefined })
              }
            />
          </div>
        </details>
      </div>
    </section>
  );
}

function NotesTab({
  action,
  readOnly,
  onUpdate,
}: {
  action: ContainmentPlanAction;
  readOnly: boolean;
  onUpdate: (patch: Partial<ContainmentPlanAction>) => void;
}): ReactElement {
  const [draft, setDraft] = useState("");
  const atCap = action.notes.length >= MAX_CONTAINMENT_NOTES_PER_ACTION;
  const addNote = (): void => {
    const text = draft.trim();
    if (readOnly || !text || atCap) return;
    onUpdate({
      notes: [
        {
          id: `note-${Date.now()}-${action.notes.length}`,
          at: Date.now(),
          text,
        },
        ...action.notes,
      ],
    });
    setDraft("");
  };

  return (
    <div className="space-y-2">
      {!readOnly ? (
        <div className="flex items-start gap-2">
          <Textarea
            aria-label="Add a note"
            placeholder="Add a progress note…"
            rows={2}
            maxLength={2000}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                event.preventDefault();
                addNote();
              }
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            className="mt-1 shrink-0"
            disabled={draft.trim() === "" || atCap}
            onClick={addNote}
          >
            Add note
          </Button>
        </div>
      ) : null}
      {action.notes.length === 0 ? (
        <EmptyState size="sm" title="No notes yet." />
      ) : (
        <ul
          aria-label="Action notes"
          className="max-h-64 space-y-2 overflow-y-auto"
        >
          {action.notes.map((note, noteIndex) => (
            <li
              key={note.id}
              className="flex min-w-0 items-start gap-2 rounded-md border border-pb-border bg-pb-card px-2.5 py-1.5 text-xs"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {note.author ? (
                    <strong className="text-pb-text">{note.author}</strong>
                  ) : null}
                  <span className="text-dense-2 text-pb-text-muted">
                    {noteTime(note.at)}
                  </span>
                </div>
                <p className="whitespace-pre-wrap break-words text-pb-text-secondary">
                  {note.text}
                </p>
              </div>
              {!readOnly ? (
                <Button
                  iconOnly
                  size="sm"
                  variant="ghost"
                  aria-label={`Delete note ${noteIndex + 1}`}
                  onClick={() =>
                    onUpdate({
                      notes: action.notes.filter(
                        (candidate) => candidate.id !== note.id,
                      ),
                    })
                  }
                  className="shrink-0 rounded text-pb-text-muted transition-colors hover:bg-pb-red/10 hover:text-pb-red-text focus-ring"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
